// Dairesel kayma yüzeyi × yüzey kırık çizgisi kesişimi ve dilimlere bölme.
// Diziler yeniden kullanılır (tarama sırasında allocation yok).
import type { Pt } from "../math/polygon";
import type { SlipCircle } from "../types/result";
import type { BaseOut, ColumnOut, GroundModel } from "./model";

export interface SliceArrays {
  n: number;
  x: Float64Array; yb: Float64Array; ys: Float64Array;
  b: number;
  W: Float64Array; yc: Float64Array;
  sinA: Float64Array; cosA: Float64Array;
  tanPhi: Float64Array; c: Float64Array; u: Float64Array;
  mat: Int8Array;                    // 0 dolgu, 1 temel, 2 duvar
  x1: number; x2: number;            // çıkış (ön) ve giriş (arka) x
}

export function allocSlices(n: number): SliceArrays {
  return {
    n, x: new Float64Array(n), yb: new Float64Array(n), ys: new Float64Array(n), b: 0,
    W: new Float64Array(n), yc: new Float64Array(n), sinA: new Float64Array(n), cosA: new Float64Array(n),
    tanPhi: new Float64Array(n), c: new Float64Array(n), u: new Float64Array(n), mat: new Int8Array(n), x1: 0, x2: 0,
  };
}

const MAT: Record<BaseOut["material"], number> = { backfill: 0, foundation: 1, wall: 2 };
const colOut: ColumnOut = { w: 0, yc: 0 };
const baseOut: BaseOut = { tanPhi: 0, c: 0, u: 0, material: "foundation" };
const roots: number[] = [];

/** Dairenin ALT yayının yüzey kırık çizgisiyle kesişim x'leri (artan, yakın kökler tekil). */
function lowerArcRoots(surface: Pt[], cx: number, cy: number, r: number): number {
  let k = 0;
  const r2 = r * r;
  for (let i = 0; i + 1 < surface.length; i++) {
    const p = surface[i]!, q = surface[i + 1]!;
    const dx = q.x - p.x, dy = q.y - p.y;
    const fx = p.x - cx, fy = p.y - cy;
    const a = dx * dx + dy * dy;
    if (a < 1e-18) continue;
    const bq = 2 * (fx * dx + fy * dy);
    const cq = fx * fx + fy * fy - r2;
    const disc = bq * bq - 4 * a * cq;
    if (disc < 0) continue;
    const sq = Math.sqrt(disc);
    for (let t = 0; t < 2; t++) {
      const s = (t === 0 ? -bq - sq : -bq + sq) / (2 * a);
      if (s < -1e-9 || s > 1 + 1e-9) continue;
      const y = p.y + s * dy;
      if (y > cy + 1e-9) continue;              // üst yay
      const x = p.x + s * dx;
      let dup = false;
      for (let j = 0; j < k; j++) if (Math.abs(roots[j]! - x) < 1e-7) { dup = true; break; }
      if (!dup) { roots[k++] = x; }
    }
  }
  // küçük ekleme sıralaması
  for (let i = 1; i < k; i++) { const v = roots[i]!; let j = i; while (j > 0 && roots[j - 1]! > v) { roots[j] = roots[j - 1]!; j--; } roots[j] = v; }
  return k;
}

/**
 * Daireyi yüzeyle kesip kayan kütleyi n eşit genişlikte dilime böler.
 * Geçersiz daire (kapalı kütle yok, izinli giriş/çıkış yok, sert tabanı geçiyor) → false.
 */
export function buildSlices(model: GroundModel, circle: SlipCircle, out: SliceArrays): boolean {
  const { cx, cy, r } = circle;
  const k = lowerArcRoots(model.surface, cx, cy, r);
  if (k < 2) return false;
  let best = -1, bestSpan = 0;
  for (let i = 0; i + 1 < k; i++) {
    const xa = roots[i]!, xb = roots[i + 1]!;
    if (xb - xa < 1e-6) continue;
    const xm = (xa + xb) / 2;
    const arc = cy - Math.sqrt(Math.max(0, r * r - (xm - cx) * (xm - cx)));
    if (model.surfaceY(xm) - arc <= 1e-9) continue;                 // yay yüzeyin üstünde: kütle yok
    if (!model.allowExit(xa) || !model.allowEntry(xb)) continue;
    if (xb - xa > bestSpan) { best = i; bestSpan = xb - xa; }
  }
  if (best < 0) return false;
  const x1 = roots[best]!, x2 = roots[best + 1]!;
  if (model.firmBaseY !== undefined) {
    const xl = Math.min(Math.max(cx, x1), x2);
    const lowest = cy - Math.sqrt(Math.max(0, r * r - (xl - cx) * (xl - cx)));
    if (lowest < model.firmBaseY - 1e-9) return false;
  }
  const n = out.n;
  const b = (x2 - x1) / n;
  out.b = b; out.x1 = x1; out.x2 = x2;
  for (let i = 0; i < n; i++) {
    const xm = x1 + (i + 0.5) * b;
    const dxr = (xm - cx) / r;
    const cosA = Math.sqrt(Math.max(0, 1 - dxr * dxr));
    const yb = cy - r * cosA;
    const ys = model.surfaceY(xm);
    if (ys - yb < -1e-6) return false;
    model.column(xm, yb, Math.max(ys, yb), colOut);
    model.base(xm, yb, baseOut);
    out.x[i] = xm; out.yb[i] = yb; out.ys[i] = ys;
    out.W[i] = colOut.w * b; out.yc[i] = colOut.yc;
    out.sinA[i] = dxr; out.cosA[i] = cosA;
    out.tanPhi[i] = baseOut.tanPhi; out.c[i] = baseOut.c; out.u[i] = baseOut.u; out.mat[i] = MAT[baseOut.material];
  }
  return true;
}
